import { ISiteLocation, PermitFieldType } from '~/client/graph/types.generated'
import { locationPermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import RecurringWorkflowSettings from '~/client/src/shared/models/RecurringWorkflowSettings'
import Workflow from '~/client/src/shared/models/Workflow'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import PermitFieldsBaseStore from '~/client/src/shared/stores/ui/PermitFieldsStores/PermitFieldsBase.store'

export default class LocationClosureValidator {
  public constructor(
    private readonly state: InitialState,
    private readonly closuresStore: ClosuresStore,
  ) {}

  private get isAdminOrFormsMaster(): boolean {
    return this.state.userActiveProjectSettings.isAdminOrFormsMaster
  }

  public validateClosuresForEntity = async (
    permit: Workflow,
    fieldsStore: PermitFieldsBaseStore,
    workflowType: string,
    recurringSettings?: RecurringWorkflowSettings,
  ): Promise<boolean> => {
    if (this.isAdminOrFormsMaster) {
      return true
    }

    const locationIds = this.getIdsForValidation(fieldsStore)

    if (!locationIds.length) {
      return true
    }

    const closures = await this.closuresStore.getRelatedClosuresByDates(
      permit.getDto().permit,
      recurringSettings,
    )

    const conflictingLocationIds =
      this.closuresStore.validateIntersectedClosures(
        closures,
        locationIds,
        workflowType,
      )

    return conflictingLocationIds.length === 0
  }

  private getIdsForValidation = ({
    fields,
    currentStepFieldIds,
    editableEntity,
    canTrackFieldChanges,
    areRequiredFieldsChanged,
    firstTwoStepsFieldIds,
  }: PermitFieldsBaseStore): string[] => {
    if (!fields?.length) {
      return []
    }

    let fieldsToValidate = fields.filter(field =>
      locationPermitFieldTypes.includes(field.type as PermitFieldType),
    )

    if (areRequiredFieldsChanged) {
      fieldsToValidate = fieldsToValidate.filter(field =>
        firstTwoStepsFieldIds.has(field.id),
      )
    }

    if (canTrackFieldChanges) {
      fieldsToValidate = fieldsToValidate.filter(
        f => f.isChanged || currentStepFieldIds.has(f.id),
      )
    }

    const allLocationFields = locationPermitFieldTypes.flatMap(fieldType =>
      editableEntity.getAllFieldsByType(fieldType),
    )

    const fieldIdsToValidateSet = new Set(fieldsToValidate.map(f => f.id))

    return Array.from(
      new Set(
        allLocationFields
          .filter(field => fieldIdsToValidateSet.has(field.fieldId))
          .flatMap(field =>
            field.values.filter((value): value is ISiteLocation => !!value?.id),
          )
          .map(value => value.id),
      ),
    )
  }
}
